import { isEntryEditable, CONFIG, type Fixture, type Prediction, type Round } from '@h2h/shared';
import { ArrowRight, Lock } from '../../../components/ui/icons';
import { countdownDetailed } from '../../../lib/format';
import { cn } from '../../../lib/cn';
import { relativeDay, shortDay, timeLabel, useNow } from '../helpers';
import { hubLabels } from './hubLabels';
import { RoundHeader } from './RoundHeader';
import { SlatePager } from './SlatePager';
import { SlateRow } from './SlateRow';
import type { PicksCtaAction } from './types';

const BYC_EXPLAINER = `Build cards on any fixtures · ${CONFIG.COMBO.BASE_POINTS} pt base on 1X2, then stack boosts. Miss the base and the card busts.`;

export function PicksCard({
  round,
  nationalTeams,
  settled,
  open,
  upcoming,
  newEntryClosed,
  flexible,
  picksK,
  picksIn,
  target,
  remaining,
  slateFixtures,
  predictionByFixture,
  selectableRounds,
  selectedRoundId,
  onSelectRound,
  onPredict,
  onOpenFixture
}: {
  round: Round | null | undefined;
  nationalTeams: boolean;
  settled: boolean;
  open: boolean;
  upcoming: boolean;
  newEntryClosed: boolean;
  flexible: boolean;
  picksK: number;
  picksIn: number;
  target: number;
  remaining: number;
  slateFixtures: Fixture[];
  predictionByFixture: Map<string, Prediction>;
  selectableRounds?: Round[];
  selectedRoundId?: string | null;
  onSelectRound?: (roundId: string) => void;
  onPredict: (action: PicksCtaAction) => void;
  onOpenFixture: (fixtureId: string) => void;
}) {
  const now = useNow(true);
  const picked = (id: string): boolean => predictionByFixture.has(id);
  if (!round) {
    const labels = hubLabels('FOOTBALL');
    return (
      <div className="mt-4 rounded-[24px] border border-black/8 bg-white p-[18px] shadow-[0_20px_44px_-26px_rgba(20,20,50,0.34)]">
        <div className="text-[15px] font-extrabold tracking-snug text-ink">{labels.emptySlateTitle}</div>
        <div className="mt-1 text-[13px] font-semibold text-ink2">{labels.emptySlateBody}</div>
      </div>
    );
  }

  const anyPickLive = slateFixtures.some((f) => f.status === 'LIVE' && picked(f.fixtureId));

  const pickedKickoffs = slateFixtures.filter((f) => picked(f.fixtureId)).map((f) => f.kickoffAt);
  const cardLocked = !isEntryEditable(round, pickedKickoffs, now);

  const slateEyebrow = flexible
    ? round.label
    : `${relativeDay(round.firstKickoffAt, now)} · ${round.label}`;

  // ── Upcoming: same layout structure as BuildOverview (no outer container) ──
  if (upcoming) {
    const upcomingTitle = round.openAt > now ? `Cards open ${relativeDay(round.openAt, now)}` : 'Opening soon';
    return (
      <>
        {selectableRounds && selectableRounds.length > 1 && onSelectRound && (
          <SlatePager
            rounds={selectableRounds}
            selectedId={selectedRoundId ?? round.roundId}
            isCycling={false}
            now={now}
            onSelect={onSelectRound}
          />
        )}
        <RoundHeader
          state="upcoming"
          eyebrow={slateEyebrow}
          title={upcomingTitle}
          subtitle={round.openAt > now ? `Opens ${shortDay(round.openAt)} · ${timeLabel(round.openAt)}` : undefined}
          deadlineLabel={round.openAt > now ? countdownDetailed(round.openAt, now) : null}
        />
        {slateFixtures.length > 0 && (
          <div className="mt-3 flex flex-col gap-[7px]">
            {slateFixtures.map((f) => (
              <SlateRow
                key={f.fixtureId}
                fixture={f}
                nationalTeams={nationalTeams}
                prediction={predictionByFixture.get(f.fixtureId)}
                now={now}
                isUrgent={false}
                preview
                onOpen={() => onOpenFixture(f.fixtureId)}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  let eyebrow: string;
  let title: string;
  let cta: string;
  let action: PicksCtaAction;
  if (settled) {
    eyebrow = `${round.label} · settled`;
    title = 'Round settled';
    cta = 'See results';
    action = 'results';
  } else if (newEntryClosed) {
    eyebrow = slateEyebrow;
    title = 'Cards closed';
    cta = 'Live board';
    action = 'board';
  } else {
    eyebrow = slateEyebrow;
    if (picksIn === 0) {
      title = 'Build your cards';
      cta = 'Build your cards';
      action = 'picks';
    } else if (remaining > 0 && !flexible) {
      title = `${remaining} card${remaining === 1 ? '' : 's'} left`;
      cta = `Finish my cards (${remaining} left)`;
      action = 'picks';
    } else if (flexible) {
      title = `${picksIn} card${picksIn === 1 ? '' : 's'} built`;
      cta = open ? 'Add more cards' : 'Live board';
      action = open ? 'picks' : 'board';
    } else {
      title = 'You’re all in';
      cta = open ? 'Review my cards' : 'Live board';
      action = open ? 'picks' : 'board';
    }
  }

  if (!settled && cardLocked) {
    cta = 'Live board';
    action = 'board';
  }

  const watching = !settled && anyPickLive;
  if (watching) {
    cta = 'Watch live';
    action = 'board';
  }

  const progressPct = Math.max(4, Math.min(100, (picksIn / Math.max(1, target)) * 100));
  const showRows = !settled && slateFixtures.length > 0;

  // Soonest still-open unpicked fixture — gets the urgent dark row treatment.
  let urgentFixtureId: string | null = null;
  let urgentRem = Infinity;
  let headerLockAt: number | null = null;
  for (const f of slateFixtures) {
    if (f.status !== 'SCHEDULED' || f.kickoffAt <= now) continue;
    if (!picked(f.fixtureId)) {
      if (headerLockAt == null || f.kickoffAt < headerLockAt) headerLockAt = f.kickoffAt;
      const rem = f.kickoffAt - now;
      if (rem < urgentRem) {
        urgentRem = rem;
        urgentFixtureId = f.fixtureId;
      }
    }
  }
  const showDeadline = !settled && !upcoming;
  const deadlineAmber = showDeadline && flexible && headerLockAt != null && picksIn === 0;
  const showProgress = !settled && !upcoming && picksIn > 0;
  const progressLabel = flexible
    ? `${picksIn} card${picksIn === 1 ? '' : 's'} built`
    : `${picksIn} of ${picksK} cards`;

  return (
    <div className="mt-3 overflow-hidden rounded-[24px] border border-black/8 bg-white shadow-[0_22px_46px_-26px_rgba(20,20,50,0.36)]">
      {selectableRounds && selectableRounds.length > 1 && onSelectRound && (
        <SlatePager
          rounds={selectableRounds}
          selectedId={selectedRoundId ?? round?.roundId ?? null}
          isCycling={false}
          now={now}
          onSelect={onSelectRound}
          embedded
        />
      )}

      {/* header — title left, live deadline pill right */}
      <div className="flex items-start justify-between gap-3 px-[18px] pb-[10px] pt-[17px]">
        <div className="min-w-0">
          <div className="truncate text-[11px] font-extrabold uppercase tracking-[0.09em] text-ink2">{eyebrow}</div>
          <div className="mt-1 text-[21px] font-extrabold tracking-[-0.028em] text-ink">{title}</div>
          {!settled && !upcoming && picksIn === 0 && (
            <p className="mt-1.5 text-[12px] font-medium leading-[1.45] text-ink2">{BYC_EXPLAINER}</p>
          )}
        </div>
        {showDeadline && (
          <div className="inline-flex shrink-0 items-center gap-[5px] self-start rounded-full border border-[rgba(255,255,255,0.08)] bg-[linear-gradient(150deg,#22232C,#0E0E14)] px-[9px] py-[5px] shadow-[0_6px_14px_-8px_rgba(0,0,0,0.5)]">
            <Lock
              width={11}
              height={11}
              strokeWidth={2.5}
              className={deadlineAmber ? 'text-[#F0B34A]' : 'text-[#3FE08B]'}
            />
            <span
              className={cn(
                'num text-[12.5px] font-extrabold tracking-[-0.01em]',
                deadlineAmber ? 'text-[#F0B34A]' : 'text-[#3FE08B]'
              )}
            >
              {headerLockAt != null
                ? `Next lock ${countdownDetailed(headerLockAt, now)}`
                : flexible && picksIn > 0
                  ? 'All open built'
                  : 'Cards closed'}
            </span>
          </div>
        )}
      </div>

      {/* progress — only once you've started building */}
      {showProgress && (
        <div className="flex items-center gap-[9px] px-[18px] pb-[14px]">
          <div className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-[#E6E6EC]">
            <div
              className="h-full rounded-[3px] bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width] duration-300 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="num shrink-0 text-[11px] font-bold text-ink2">{progressLabel}</span>
        </div>
      )}

      {/* slate rows */}
      {showRows && (
        <div className="flex flex-col gap-[7px] px-3">
          {slateFixtures.map((f) => (
            <SlateRow
              key={f.fixtureId}
              fixture={f}
              nationalTeams={nationalTeams}
              prediction={predictionByFixture.get(f.fixtureId)}
              now={now}
              isUrgent={f.fixtureId === urgentFixtureId}
              preview={upcoming}
              onOpen={() => onOpenFixture(f.fixtureId)}
            />
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="p-[14px]">
        {upcoming ? (
          <div className="flex w-full items-center justify-center gap-2 rounded-[15px] border border-black/8 bg-fill/60 p-[15px]">
            <Lock width={14} height={14} strokeWidth={2.6} className="text-ink3" />
            <span className="text-[14px] font-extrabold tracking-snug text-ink2">
              {round.openAt > now ? `Opens ${shortDay(round.openAt)} · ${timeLabel(round.openAt)}` : 'Opening soon'}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onPredict(action)}
            className="tap flex w-full items-center justify-center gap-2 rounded-[15px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] p-[15px] shadow-[0_16px_30px_-16px_rgba(11,160,120,0.7)] active:scale-[0.99]"
          >
            {watching && <span className="h-[8px] w-[8px] animate-h2h-pulse rounded-full bg-white" />}
            <span className="text-[15px] font-extrabold tracking-[-0.01em] text-white">{cta}</span>
            <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
          </button>
        )}
      </div>
    </div>
  );
}
